import React, { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from 'recharts';
import api from '../services/api';
import { PERIOD_PRESETS, computeDateRange } from '../utils/jdmaPeriod';
import './JDMAVue.css';
import './JDMASuiviVN.css';

function formatMois(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
}

function PctBadge({ value, threshold = 70 }) {
  if (value == null) return <span className="suivi-pct suivi-pct-na">—</span>;
  const cls = value >= threshold ? 'good' : value >= threshold - 20 ? 'warn' : 'bad';
  return <span className={`suivi-pct suivi-pct-${cls}`}>{value}%</span>;
}

function JDMASuiviVN({ user, concessions = [], periodFilter, setPeriodFilter }) {
  // Fallback local si le composant est monté sans props (rétro-compat / tests).
  const [localPeriod, setLocalPeriod] = useState({ preset: '30d', from: null, to: null });
  const effectivePeriod = periodFilter || localPeriod;
  const updatePeriod = setPeriodFilter || setLocalPeriod;

  // Concession : celle choisie dans le menu (2026-10-26). La page est remontée
  // quand le choix du menu change, une initialisation suffit.
  const [filtreConc] = useState(() => (user?.concession_id ? String(user.concession_id) : ''));
  const [search, setSearch]         = useState('');

  const [stats, setStats]           = useState(null);
  const [evolution, setEvolution]   = useState([]);
  const [collaborateurs, setCollab] = useState([]);
  const [loading, setLoading]       = useState(true);

  // Bornes from/to issues du filtre période partagé (helper commun à APV/VN)
  const { from, to } = useMemo(() => computeDateRange(effectivePeriod), [effectivePeriod]);

  // Nombre de mois couverts par la plage (pour l'endpoint /evolution).
  const months = useMemo(() => {
    if (!from || !to) return 6;
    const ms = new Date(to) - new Date(from);
    return Math.min(Math.max(Math.ceil(ms / (30 * 86400 * 1000)), 1), 24);
  }, [from, to]);

  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ from, to });
        if (filtreConc) params.set('concession_id', filtreConc);
        const evoParams = new URLSearchParams({ months: String(months) });
        if (filtreConc) evoParams.set('concession_id', filtreConc);

        const opt = { signal: ac.signal };
        const [statsRes, evoRes, colRes] = await Promise.all([
          api.get(`/jdma/vn/suivi/stats?${params}`, opt),
          api.get(`/jdma/vn/suivi/evolution?${evoParams}`, opt),
          api.get(`/jdma/vn/suivi/par-collaborateur?${params}`, opt),
        ]);
        if (cancelled || ac.signal.aborted) return;
        setStats(statsRes.data);
        setEvolution(evoRes.data.series || []);
        setCollab(colRes.data.collaborateurs || []);
      } catch (err) {
        if (cancelled || ac.signal.aborted) return;
        if (err.name === 'CanceledError' || err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
        console.error('Erreur chargement Suivi VN:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [from, to, filtreConc, months]);

  const collabFiltres = useMemo(() => {
    if (!search) return collaborateurs;
    const q = search.toLowerCase();
    return collaborateurs.filter(c => (c.collaborateur || '').toLowerCase().includes(q));
  }, [collaborateurs, search]);

  return (
    <div className="jdma-vue">
      <div className="jdma-vue-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher un collaborateur..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className={`page-select ${effectivePeriod.preset !== '30d' ? 'active-filter' : ''}`}
          value={effectivePeriod.preset}
          onChange={(e) => updatePeriod({ ...effectivePeriod, preset: e.target.value })}
        >
          {PERIOD_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        {effectivePeriod.preset === 'custom' && (
          <>
            <input
              type="date"
              className="page-select"
              value={effectivePeriod.from || ''}
              onChange={(e) => updatePeriod({ ...effectivePeriod, from: e.target.value })}
              title="Date de début"
            />
            <input
              type="date"
              className="page-select"
              value={effectivePeriod.to || ''}
              onChange={(e) => updatePeriod({ ...effectivePeriod, to: e.target.value })}
              title="Date de fin"
            />
          </>
        )}
        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
      </div>

      {/* KPIs : 2 taux globaux + total avis */}
      <div className="jdma-vue-kpis suivi-kpis">
        <div className="jdma-kpi">
          <div className="jdma-kpi-label">Total avis</div>
          <div className="jdma-kpi-big">{stats?.total ?? '—'}</div>
        </div>
        <div className="jdma-kpi jdma-kpi-primary">
          <div className="jdma-kpi-label">📞 Taux de recontact</div>
          <div className="jdma-kpi-big">{stats?.pct_recontacte != null ? `${stats.pct_recontacte}%` : '—'}</div>
          {stats?.nb_recontacte_repondu != null && (
            <div className="jdma-kpi-sub">{stats.nb_recontacte_oui} / {stats.nb_recontacte_repondu} réponses</div>
          )}
        </div>
        <div className="jdma-kpi jdma-kpi-success">
          <div className="jdma-kpi-label">🚗 Taux d'essai proposé</div>
          <div className="jdma-kpi-big">{stats?.pct_essai != null ? `${stats.pct_essai}%` : '—'}</div>
          {stats?.nb_essai_repondu != null && (
            <div className="jdma-kpi-sub">{stats.nb_essai_oui} / {stats.nb_essai_repondu} réponses</div>
          )}
        </div>
      </div>

      {/* Évolution mensuelle pour la concession */}
      <div className="jdma-vue-card">
        <h3 className="jdma-vue-card-title">
          Évolution mensuelle{filtreConc ? ` — ${user?.concession_nom || concessions.find(c => String(c.id) === String(filtreConc))?.nom || ''}` : ' — toutes concessions'}
        </h3>
        {evolution.length === 0 ? (
          <div className="jdma-vue-empty">Pas encore de données pour cette période.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={evolution.map(s => ({ ...s, mois_label: formatMois(s.mois) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" />
              <XAxis dataKey="mois_label" stroke="var(--text-secondary)" />
              <YAxis domain={[0, 100]} stroke="var(--text-secondary)" tickFormatter={v => `${v}%`} />
              <Tooltip formatter={(v, name) => v == null ? '—' : (name.startsWith('Taux') ? `${v}%` : v)} />
              <Legend />
              <Line type="monotone" dataKey="pct_recontacte" stroke="var(--color-accent)" strokeWidth={3} name="Taux de recontact" dot={{ r: 4 }} />
              <Line type="monotone" dataKey="pct_essai" stroke="var(--color-success)" strokeWidth={3} name="Taux d'essai proposé" dot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Tableau par collaborateur */}
      <div className="jdma-vue-card">
        <h3 className="jdma-vue-card-title">Par collaborateur ({collabFiltres.length})</h3>
        {loading ? (
          <div className="jdma-vue-empty">Chargement…</div>
        ) : collabFiltres.length === 0 ? (
          <div className="jdma-vue-empty">Aucun collaborateur avec avis sur cette période.</div>
        ) : (
          <div className="table-container">
            <table className="suivi-table">
              <thead>
                <tr>
                  <th rowSpan={2}>Collaborateur</th>
                  <th rowSpan={2}>Nb avis</th>
                  <th colSpan={3} className="suivi-th-recontact">📞 Recontact</th>
                  <th colSpan={3} className="suivi-th-essai">🚗 Essai proposé</th>
                </tr>
                <tr>
                  <th className="suivi-th-recontact">Oui</th>
                  <th className="suivi-th-recontact">Réponses</th>
                  <th className="suivi-th-recontact">Taux</th>
                  <th className="suivi-th-essai">Oui</th>
                  <th className="suivi-th-essai">Réponses</th>
                  <th className="suivi-th-essai">Taux</th>
                </tr>
              </thead>
              <tbody>
                {collabFiltres.map(c => (
                  <tr key={c.collaborateur}>
                    <td className="suivi-collab">{c.collaborateur}</td>
                    <td className="suivi-num">{c.nb_avis}</td>
                    <td className="suivi-num">{c.nb_recontacte_oui}</td>
                    <td className="suivi-num suivi-muted">{c.nb_recontacte_repondu}</td>
                    <td className="suivi-num"><PctBadge value={c.pct_recontacte} threshold={70} /></td>
                    <td className="suivi-num">{c.nb_essai_oui}</td>
                    <td className="suivi-num suivi-muted">{c.nb_essai_repondu}</td>
                    <td className="suivi-num"><PctBadge value={c.pct_essai} threshold={70} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default JDMASuiviVN;
